import {
  Table,
  Tabs,
  Typography,
  Button,
  Row,
  Col,
  Form,
  Select,
  message,
  Modal,
  TableColumnsType
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import {
  downloadTemplateNutritionStandard,
  getHistory,
  getListAgeNutritionStandard,
  getListHealthStatusNutritionStandard,
  importFile,
  watchListAgeNutritionStandard,
  watchListHealthStatusNutritionStandard,
  watchListHistory,
  watchNutritionLoading,
  watchPaginationHistory
} from '@/redux/slices/nutrition-standards-management.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { formatDateTime } from '@/utils/common';
import CustomLabel from '@/components/custom-label';
import { MESSAGE } from '@/constants/message.constant';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
};

export default function NutritionStandardImport() {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<string>('INPUT');
  const [age, setAge] = useState<string>();
  const [healthCondition, setHealthCondition] = useState<string[]>([]);
  const [form] = Form.useForm();
  const { push } = useQuery();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const loading = useAppSelector(watchNutritionLoading);
  const listAge = useAppSelector(watchListAgeNutritionStandard);
  const listHealthStatus = useAppSelector(
    watchListHealthStatusNutritionStandard
  );
  const pagination = useAppSelector(watchPaginationHistory);
  const listHistory = useAppSelector(watchListHistory);
  useEffect(() => {
    dispatch(getListAgeNutritionStandard('NUTRITIONAL_STANDARD_IMPORT'));
    dispatch(getListHealthStatusNutritionStandard());
    dispatch(getHistory({}));
  }, [dispatch]);

  const ageDishGroupOptions = listAge.map((item) => ({
    value: item.id,
    label: item.description
  }));

  const heathStatusGroupOptions = listHealthStatus.map((item) => ({
    value: item.healthStatusId,
    label: item.displayName
  }));

  const tabItems = [
    {
      key: 'INPUT',
      label: 'Nhập dữ liệu'
    },
    {
      key: 'HISTORY',
      label: 'Lịch sử nhập dữ liệu'
    }
  ];

  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      render: (_, __, index) =>
        (pagination.curPage - 1) * pagination.limitPage + index + 1,
      width: 100
    },
    {
      title: 'Tiêu chuẩn dinh dưỡng dành cho',
      dataIndex: 'healthStatusName',
      key: 'healthStatusName',
      width: 250
    },
    {
      title: 'Độ tuổi',
      dataIndex: 'ageRangeName',
      key: 'ageRangeName',
      width: 200
    },
    {
      title: 'Tên file',
      dataIndex: 'name',
      key: 'name',
      width: 400
    },
    {
      title: 'Tải file xuống',
      dataIndex: 'url',
      key: 'url',
      render: (url: string, record: any) => {
        return (
          <div>
            <Button
              css={downloadHistoryButton}
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(url, record.name)}
            ></Button>
          </div>
        );
      },
      width: 100
    },
    {
      title: 'Ngày nhập thực đơn',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 200,
      render: (record) => (record ? formatDateTime(record) : '')
    },
    {
      title: 'Người nhập',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 200
    }
  ];

  const submitImport = async (mode?: 'replace' | 'skip') => {
    if (!file || !age || !healthCondition || healthCondition.length === 0) {
      message.warning('Vui lòng chọn đầy đủ thông tin và file để tải lên.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('AGE_RANGE_ID', age);
    formData.append('HEALTH_STATUS_IDS', JSON.stringify(healthCondition));
    if (mode === 'replace') {
      formData.append('REPLACE_EXISTING', '1');
    }
    if (mode === 'skip') {
      formData.append('SKIP_DUPLICATES', '1');
    }

    try {
      await dispatch(importFile(formData)).unwrap();
      form.resetFields();
      setFile(null);
      setAge(undefined);
      setHealthCondition([]);
      push(PageURLs.ofNutritionStandardsManagement());
    } catch (error: any) {
      const status = error?.status;
      const errorMessage = error?.message ?? error;
      if (status === 409) {
        const duplicateRows: number[] = Array.isArray(error?.data?.duplicateRows)
          ? error.data.duplicateRows
          : [];
        const MAX_DUPLICATE_DISPLAY = 100;
        const isTruncated = duplicateRows.length > 150;
        const visibleRows = isTruncated
          ? duplicateRows.slice(0, MAX_DUPLICATE_DISPLAY)
          : duplicateRows;
        const rowsText =
          visibleRows.length > 0
            ? `Tiêu chuẩn dinh dưỡng Dòng ${visibleRows.join(', ')}${isTruncated ? ',......' : ''}`
            : 'Tiêu chuẩn dinh dưỡng đang bị trùng với tiêu chuẩn dinh dưỡng hiện tại.';

        Modal.confirm({
          title: 'Dữ liệu đã tồn tại',
          content: (
            <div>
              <div>{rowsText}</div>
              <div
                style={{
                  marginTop: 4,
                  fontStyle: 'italic',
                  fontSize: '90%',
                  color: '#bdbdbd'
                }}
              >
                Bạn có muốn thay thế?
              </div>
            </div>
          ),
          okText: 'Tiếp tục',
          cancelText: 'Hủy',
          onOk: async () => {
            await submitImport('replace');
          },
          onCancel: async () => {
            await submitImport('skip');
          }
        });
        return;
      }

      if (errorMessage) {
        message.error(errorMessage as string);
        return;
      }

      message.error(MESSAGE.IMPORT_COMBO_FOOD_FAIL);
    }
  };

  const handleUpload = async () => {
    await submitImport();
  };

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofNutritionStandardsManagement());
  }, [push]);

  const handleDelFile = () => {
    setFile(null);
  };

  const handleDownload = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'downloaded-file';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const allowedExtensions = ['.xlsx', '.xls', '.csv'];
  const isValidFile = (file: File) => {
    const name = file.name.toLowerCase();
    return allowedExtensions.some(ext => name.endsWith(ext));
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const MAX_SIZE = 100 * 1024 * 1024; // 100MB
      if (file.size > MAX_SIZE) {
        message.error('Dung lượng file không được vượt quá 100MB');
        return;
      }
      if (!isValidFile(file)) {
        message.error('Vui lòng chọn tệp excel tiêu chuẩn');
        return;
      }
      setFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const MAX_SIZE = 100 * 1024 * 1024; // 100MB
      if (file.size > MAX_SIZE) {
        message.error('Dung lượng file không được vượt quá 100MB');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      if (!isValidFile(file)) {
        message.error('Vui lòng chọn tệp excel tiêu chuẩn');
        return;
      }
      setFile(file);
    }
  };

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit
    }: PageChangeParams) => {
      dispatch(
        getHistory({
          offset: page,
          limit: pageSize
        })
      );
    },
    [dispatch]
  );

  const handleDownLoadTemplateNutritionStandard = async () => {
    const template = await dispatch(downloadTemplateNutritionStandard()).unwrap();
    if (template) {
      handleDownload(template.URL, template.NAME);
    }
  }

  return (
    <>
      <Form form={form} css={rootStyles}>
        <Row css={boxStyles} align='middle' justify='space-between' gutter={24}>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            Nhập tiêu chuẩn dinh dưỡng theo danh sách
          </Typography.Title>

          <Button
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
          />
        </Row>

        <Tabs
          css={tabStyle}
          defaultActiveKey='INPUT'
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
        >
          {tabItems.map((item) => (
            <Tabs.TabPane tab={item.label} key={item.key} />
          ))}
        </Tabs>
        {activeTab === 'INPUT' && (
          <>
            <div css={boxStyles}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={CustomLabel({ text: 'Thực đơn xây dựng cho tình trạng sức khoẻ', isRequired: true })}
                    name='healthCondition'
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: 'Vui lòng chọn tình trạng sức khoẻ'
                      }
                    ]}
                  >
                    <Select
                      mode='multiple'
                      options={heathStatusGroupOptions}
                      onChange={(value) => setHealthCondition(value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={CustomLabel({ text: 'Độ tuổi', isRequired: true })}
                    name='ageRange'
                    required={false}
                    rules={[
                      { required: true, message: 'Vui lòng chọn độ tuổi' }
                    ]}
                  >
                    <Select
                      options={ageDishGroupOptions}
                      onChange={(value) => setAge(value)}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Upload buttons */}
              <Row justify='center' style={{ marginBottom: 16 }}>
                <Button
                  type='default'
                  icon={<UploadOutlined />}
                  css={downloadButton}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={!age || !healthCondition || healthCondition.length === 0}
                >
                  Tải lên
                </Button>
                <Button
                  type='link'
                  onClick={() => handleDownLoadTemplateNutritionStandard()}
                >
                  Tải xuống template
                </Button>
              </Row>

              {/* Selected file name & delete */}
              {file && (
                <Row
                  justify='space-between'
                  align='middle'
                  style={{ marginBottom: 16 }}
                >
                  <Typography.Text>
                    <PaperClipOutlined style={{ marginRight: 8 }} />
                    <span style={{ color: '#1890ff' }}>{file.name}</span>
                  </Typography.Text>
                  <DeleteOutlined
                    style={{
                      fontSize: 18,
                      color: '#ff4d4f',
                      cursor: 'pointer'
                    }}
                    onClick={handleDelFile}
                  />
                </Row>
              )}

              {/* File upload drop zone */}
              <div css={boxStyles}>
                <label
                  htmlFor='file-upload'
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  style={{
                    display: 'block',
                    height: 100,
                    border: '1px dashed #ccc',
                    textAlign: 'center',
                    padding: '36px 0',
                    cursor: (!age || !healthCondition || healthCondition.length === 0) ? 'not-allowed' : 'pointer',
                    background: (!age || !healthCondition || healthCondition.length === 0) ? '#f5f5f5' : 'white'
                  }}
                  onClick={() => {
                    if (!age || !healthCondition || healthCondition.length === 0) return;
                    if (file) return;
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                      fileInputRef.current.click();
                    }
                  }}
                >
                  Kéo thả file vào đây hoặc nhấn "Tải lên"
                  <input
                    id='file-upload'
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* Submit button */}
              <Row justify='center' style={{ marginTop: 20 }}>
                <Button type='primary' onClick={handleUpload} disabled={!file}>
                  Bắt đầu tải lên
                </Button>
              </Row>
            </div>
          </>
        )}

        {activeTab === 'HISTORY' && (
          <Table<any>
            columns={columns}
            dataSource={listHistory}
            loading={loading}
            pagination={{
              showQuickJumper: false,
              showSizeChanger: true,
              current: pagination.curPage,
              pageSize: pagination.limitPage,
              total: pagination.totalRows,
              onChange: (page, pageSize) => {
                handlePageChange({ page, pageSize });
              }
            }}
          />
        )}
      </Form>
    </>
  );
}

// const styleFooter = css`
//   min-height: calc(12vh - var(--header-height));
//   display: flex;
//   flex-direction: column;
//   justify-content: center;
//   align-items: center;
//   gap: 1rem;
//   padding: 0 1rem;
//   background-color: var(--white-color);
//   .ant-btn-lg {
//     width: 30rem;
//   }
// `;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;

const downloadButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const downloadHistoryButton = css`
  background: white;
  border: none;
  color: rgba(110, 185, 255, 1);
  display: flex;
  justify-content: center;
  align-items: center;

  .anticon {
    font-size: 18px;
    color: rgba(110, 185, 255, 1);
  }

  &:hover {
    background: #f0f0f0;
    color: rgba(90, 165, 235, 1);
  }
`;

